/**
 * CrisisResources — prominent, always-accessible crisis banner.
 *
 * Rendered whenever a safety trigger fires (patient questionnaire) or a chat
 * message is escalated. It always shows the hardcoded national resources
 * (988 / Crisis Text Line / 911) regardless of what the backend returns.
 */

import React from 'react';
import { Phone, LifeBuoy } from 'lucide-react';
import { CRISIS_RESOURCES_FALLBACK } from './types';

interface CrisisResourcesProps {
  /** Optional heading override. */
  title?: string;
  className?: string;
}

export const CrisisResources: React.FC<CrisisResourcesProps> = ({
  title = 'You are not alone — help is available right now',
  className = '',
}) => {
  return (
    <div
      role="alert"
      aria-live="assertive"
      className={`rounded-lg border-2 border-red-500 bg-red-50 p-5 text-red-900 shadow-sm ${className}`}
    >
      <div className="flex items-start gap-3">
        <LifeBuoy className="mt-0.5 h-6 w-6 shrink-0 text-red-600" aria-hidden="true" />
        <div className="flex-1">
          <h3 className="text-base font-semibold text-red-800">{title}</h3>
          <p className="mt-1 text-sm text-red-800">
            If you are thinking about harming yourself or are in crisis, please reach
            out now. Support is free, confidential, and available 24/7.
          </p>
          <ul className="mt-4 space-y-3">
            {CRISIS_RESOURCES_FALLBACK.map((r) => (
              <li
                key={r.name}
                className="flex flex-col gap-1 rounded-md border border-red-200 bg-white p-3 sm:flex-row sm:items-center sm:justify-between"
              >
                <div>
                  <p className="font-semibold text-red-900">{r.name}</p>
                  <p className="text-sm text-red-700">{r.detail}</p>
                </div>
                <a
                  href={r.href}
                  className="inline-flex items-center justify-center gap-2 rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-400 focus:ring-offset-2"
                >
                  <Phone className="h-4 w-4" aria-hidden="true" />
                  {r.action}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

/**
 * ProvisionalNormsDisclaimer — reusable caveat shown on clinician surfaces.
 */
export const ProvisionalNormsDisclaimer: React.FC<{ className?: string }> = ({
  className = '',
}) => (
  <p
    className={`rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 ${className}`}
  >
    <strong>Provisional norms — not a validated clinical instrument.</strong>{' '}
    Scores are experimental and must not be used as the sole basis for diagnosis
    or treatment decisions. Clinical judgment is required.
  </p>
);
